import dayjs from 'dayjs';

import { useNavigate } from 'hooks';
import { mapQuery } from 'utils/query';
import { Flex } from 'components/flex';
import { Info } from 'components/info';
import { Row, Col } from 'components/grid';
import { InfoBlock } from 'components/info-block';
import { GeneralInfo } from 'components/general-info';
import { DeleteDialog } from 'components/delete-dialog';
import { formatPhone, joinStrings } from 'utils/helpers';
import { Details as DetailsComponent } from 'components/details';
import { useGetQuery, useRemoveMutation } from 'store/api/customers';

import { DetailsProps } from './types';
import { Checkouts } from './checkouts';

export const Details = ({ id, navigateUrlAfterDelete }: DetailsProps) => {
  const navigate = useNavigate();

  const { data, ...props } = useGetQuery(id!, { skip: !id });
  const [remove, mutation] = useRemoveMutation();

  const name = data && `${data.person.firstName} ${data.person.lastName}`;

  return (
    <DetailsComponent {...mapQuery(props)}>
      {data && (
        <GeneralInfo
          name={name}
          photoSize={128}
          withPlaceholders
          stickyWithHeader={false}
          photo={data.person.photoUrl}
          onEdit={() => navigate('edit')}
          rightSlot={
            <DeleteDialog
              name={name!}
              id={data.id}
              action={remove}
              mutation={mutation}
              title="Delete customer"
              navigateUrl={navigateUrlAfterDelete}
            />
          }
          sections={[
            {
              rows: [
                [
                  {
                    col: 4,
                    title: 'Phone number',
                    value: formatPhone(data.person.phone),
                  },
                  {
                    col: 4,
                    title: 'Email',
                    value: data.person.email,
                  },
                  {
                    col: 4,
                    title: 'Additions phone numbers',
                    value: joinStrings(
                      data?.additionalPhones?.map((phone) =>
                        formatPhone(phone)
                      ),
                      ', '
                    ),
                  },
                  {
                    col: 4,
                    title: 'Additions emails',
                    value: joinStrings(data?.additionalEmails, ', '),
                  },
                  {
                    col: 4,
                    title: 'Birthday',
                    value:
                      data.person.birthday &&
                      dayjs(data.person.birthday).format('LL'),
                  },
                ],
                [
                  {
                    col: 4,
                    colTablet: 6,
                    title: 'Address',
                    value: data.person.address,
                  },
                ],
              ],
            },
            ...(data.shipments.length > 0
              ? [
                  {
                    title: 'Shipment info',
                    children: (
                      <Flex column gap={16} style={{ paddingBottom: '24px' }}>
                        {data.shipments.map((shipment) => (
                          <InfoBlock key={shipment.id}>
                            <Row>
                              <Col col={3}>
                                <Info
                                  title="Address"
                                  value={shipment.address}
                                />
                              </Col>
                              <Col col={3}>
                                <Info
                                  title="Contact Name"
                                  value={shipment.contactName}
                                />
                              </Col>
                              <Col col={3}>
                                <Info
                                  title="Phone"
                                  value={formatPhone(shipment.phone)}
                                />
                              </Col>
                            </Row>

                            <Row>
                              <Col col={3}>
                                <Info
                                  title="Emergency Phone"
                                  value={formatPhone(shipment.emergencyPhone)}
                                />
                              </Col>

                              <Col col={3}>
                                <Info title="Email" value={shipment.email} />
                              </Col>
                            </Row>
                          </InfoBlock>
                        ))}
                      </Flex>
                    ),
                  },
                ]
              : []),
            {
              title: 'Registered info',
              rows: [
                [
                  {
                    col: 4,
                    title: 'Employees',
                    value: joinStrings(
                      data?.employees?.map((employee) => employee?.name) || [],
                      ', '
                    ),
                  },
                  {
                    col: 4,
                    title: 'Registered',
                    value: data.registered,
                  },
                  {
                    col: 4,
                    title: 'Event',
                    value: data.event?.name,
                  },
                ],
              ],
            },
          ]}
        />
      )}

      <Checkouts id={id} />
    </DetailsComponent>
  );
};
